import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Send, Search, MessageCircle, User } from 'lucide-react';
import useWebSocket from '../hooks/useWebSocket';
import MessageList from '../components/MessageList';
import MessageInput from '../components/MessageInput';

function ConversationList({ matches, activeId, searchTerm, onSearch, onSelect, wsConnected }) {
  const filtered = matches.filter(m => !searchTerm || m.other_user?.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="w-80 lg:w-96 h-full flex flex-col shrink-0" style={{borderRight:'1px solid var(--border-color)'}}>
      <div className="p-4" style={{borderBottom:'1px solid var(--border-color)'}}>
        <h2 className="text-lg font-black mb-3" style={{color:'var(--text-primary)'}}>Messages</h2>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{color:'var(--text-faint)'}} />
          <input value={searchTerm} onChange={e => onSearch(e.target.value)} placeholder="Rechercher..." className="input-field !pl-10 !py-2.5 !text-sm !rounded-full" data-testid="chat-search" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageCircle size={28} className="mx-auto mb-2" style={{color:'var(--text-faint)'}} />
            <p className="text-sm" style={{color:'var(--text-muted)'}}>Aucune conversation</p>
          </div>
        ) : filtered.map(match => {
          const other = match.other_user;
          const isActive = activeId === match.id;
          return (
            <div key={match.id} onClick={() => onSelect(match)}
              className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-all ${isActive ? 'bg-brand-violet/10' : 'hover:bg-[var(--bg-card-hover)]'}`}
              style={isActive ? {borderLeft:'3px solid #A78BFA'} : {borderLeft:'3px solid transparent'}}
              data-testid={`conv-${match.id}`}
            >
              <div className="w-11 h-11 rounded-full overflow-hidden shrink-0" style={{background:'var(--bg-card)'}}>
                {other?.photos?.[0] ? (
                  <img src={other.photos[0]} alt={other.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-bold text-sm" style={{color:'var(--text-muted)'}}>{other?.name?.[0]}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{color:'var(--text-primary)'}}>{other?.name}, {other?.age}</p>
                <p className="text-xs truncate" style={{color:'var(--text-muted)'}}>{other?.city || 'Cliquez pour discuter'}</p>
              </div>
              {wsConnected && isActive && <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChatPanel({ activeMatch, messages, msgLoading, otherTyping, wsConnected, userId, onSend, onTyping }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!activeMatch) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <MessageCircle size={48} className="mx-auto mb-4" style={{color:'var(--text-faint)'}} />
          <p className="text-lg font-semibold mb-1" style={{color:'var(--text-secondary)'}}>Selectionnez une conversation</p>
          <p className="text-sm" style={{color:'var(--text-muted)'}}>Choisissez un match pour commencer a discuter</p>
        </div>
      </div>
    );
  }

  const other = activeMatch.other_user;

  return (
    <div className="flex-1 flex flex-col h-full">
      <div className="px-6 py-4 flex items-center gap-4" style={{borderBottom:'1px solid var(--border-color)'}}>
        <div className="w-10 h-10 rounded-full overflow-hidden" style={{background:'var(--bg-card)'}}>
          {other?.photos?.[0] ? (
            <img src={other.photos[0]} alt={other.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-bold" style={{color:'var(--text-muted)'}}>{other?.name?.[0]}</div>
          )}
        </div>
        <div className="flex-1">
          <p className="font-bold text-sm" style={{color:'var(--text-primary)'}}>{other?.name}</p>
          <p className="text-xs" style={{color: otherTyping ? '#a855f7' : 'var(--text-muted)'}}>
            {otherTyping ? "En train d'ecrire..." : wsConnected ? 'En ligne' : 'Hors ligne'}
          </p>
        </div>
        {wsConnected && <div className="w-2.5 h-2.5 rounded-full bg-green-500" />}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {msgLoading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-brand-violet border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <MessageList messages={messages} userId={userId} />
        )}
        <div ref={bottomRef} />
      </div>

      <MessageInput onSend={onSend} onTyping={onTyping} />
    </div>
  );
}

export default function ChatHub() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [activeMatch, setActiveMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    api.get('/matches').then(r => setMatches(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadMessages = useCallback(async (matchId) => {
    if (!matchId) return;
    try {
      const res = await api.get(`/chat/messages/${matchId}`);
      setMessages(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const handleWsMessage = useCallback((data) => {
    if (data.type === 'new_message') {
      setMessages(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        return [...prev, {
          id: data.id, sender_id: data.sender_id,
          content: data.text, text: data.text,
          created_at: data.timestamp, read: data.read,
          reactions: data.reactions || [],
        }];
      });
    }
  }, []);

  const { connected, otherTyping, sendMessage, sendTyping } = useWebSocket(
    activeMatch?.id, user?.id, handleWsMessage
  );

  const selectMatch = useCallback(async (match) => {
    setActiveMatch(match);
    setMsgLoading(true);
    setMessages([]);
    await loadMessages(match.id);
    setMsgLoading(false);
  }, [loadMessages]);

  const handleSend = useCallback(async (text) => {
    if (!activeMatch) return;
    const sentViaWs = sendMessage(text);
    if (!sentViaWs) {
      try {
        await api.post('/chat/send', { match_id: activeMatch.id, content: text, text, type: 'text' });
        await loadMessages(activeMatch.id);
      } catch (e) { /* ignore */ }
    }
  }, [activeMatch, sendMessage, loadMessages]);

  if (loading) {
    return <div className="h-full flex items-center justify-center"><div className="w-8 h-8 border-2 border-brand-violet border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="h-full flex" data-testid="chat-hub-page">
      <ConversationList
        matches={matches} activeId={activeMatch?.id}
        searchTerm={searchTerm} onSearch={setSearchTerm}
        onSelect={selectMatch} wsConnected={connected}
      />
      <ChatPanel
        activeMatch={activeMatch} messages={messages}
        msgLoading={msgLoading} otherTyping={otherTyping}
        wsConnected={connected} userId={user?.id}
        onSend={handleSend} onTyping={sendTyping}
      />
    </div>
  );
}
